import FullCalendar from '@fullcalendar/react';
import {
  differenceInDays,
  endOfMonth,
  endOfWeek,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { useExternalCalendarFullCalendarEvents } from 'features/external-connections';
import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useTasks, useTaskTodayControls } from 'features/tasks';
import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FullCalendarMonth } from 'shared/components/ui/full-calendar-month';
import { weekdaysNumberMap } from 'shared/constants';
import { useOpenTaskDetail } from 'shared/contexts/task-detail';
import { useToday } from 'shared/contexts/today';
import { useCalendarEventControls } from 'shared/hooks/use-calendar-event-controls';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useUser } from 'shared/hooks/use-user';
import { Paths } from 'shared/routes';
import { PageEvents } from 'shared/services/tracking';
import { ScheduleEntryState } from 'shared/types/habit-schedule';
import { ID } from 'shared/types/id';
import { TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import { replaceUrlParams } from 'shared/utils/replace-url-params';
import { getScheduleByDate } from 'shared/utils/schedules';

import { useGoalDeadlineEvents } from '../../hooks/use-goal-deadline-events';
import { useTaskEvents } from '../../hooks/use-task-events';
import * as Styled from './month.style';

const eventClasNames = [Styled.EventContainer.styledComponentId];

export type MonthProps = {
  fullCalendarRef?: React.RefObject<FullCalendar | null>;
  referenceDate?: Timestamp;
};

export const Month: React.FC<MonthProps> = ({
  fullCalendarRef,
  referenceDate,
}) => {
  useTrackPageVisitEvent(PageEvents.UpcomingMonth, undefined);
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);
  const tasks = useTasks();
  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();
  const today = useToday();
  const openTaskDetail = useOpenTaskDetail();
  const { onComplete } = useTaskTodayControls();
  const navigate = useNavigate();

  const startDate = useMemo(
    () => startOfMonth(referenceDate || today),
    [referenceDate, today],
  );
  const dayRange = useMemo(
    () => differenceInDays(endOfMonth(startDate), startDate) + 1,
    [startDate],
  );
  const externalStartDate = useMemo(
    () =>
      startOfWeek(startDate, { weekStartsOn: weekdaysNumberMap[weekStartsOn] }),
    [startDate, weekStartsOn],
  );
  const externalDayRange = useMemo(
    () =>
      differenceInDays(
        endOfWeek(endOfMonth(startDate), {
          weekStartsOn: weekdaysNumberMap[weekStartsOn],
        }),
        externalStartDate,
      ),
    [externalStartDate, startDate, weekStartsOn],
  );

  const filteredTasks = useMemo(
    () =>
      tasks.filter(
        (task) =>
          task.type !== TaskType.Habit || !user?.settings?.upcoming?.hideHabits,
      ),
    [tasks, user?.settings?.upcoming?.hideHabits],
  );

  const taskEvents = useTaskEvents(filteredTasks, {
    rangeReferenceDate: startDate,
    dayRange,
    hideCompleted: user?.settings?.upcoming?.hideCompleted,
  });
  const goalDeadlineEvents = useGoalDeadlineEvents();
  const externalCalendarEvents = useExternalCalendarFullCalendarEvents(
    externalStartDate,
    externalDayRange,
  );
  const { onEventChange, onDrop, onDateTime } = useCalendarEventControls(
    lifeAreas,
    goals,
    tasks,
  );

  const _openEventDetail = useCallback(
    (id: ID) => {
      const task = filteredTasks.find(({ id: taskId }) => taskId === id);
      if (task) {
        openTaskDetail(task);
        return;
      }

      const goal = goals.find(({ id: goalId }) => goalId === id);
      if (goal) {
        navigate(replaceUrlParams(Paths.GoalDetail, { id: goal.id }));
        return;
      }

      const lifeArea = lifeAreas.find(
        ({ id: lifeAreaId }) => lifeAreaId === id,
      );
      if (lifeArea) {
        navigate(replaceUrlParams(Paths.LifeAreaDetail, { id: lifeArea.id }));
        return;
      }
    },
    [filteredTasks, goals, lifeAreas, openTaskDetail, navigate],
  );

  const _onComplete = useCallback(
    (id: ID, date: Timestamp) => {
      const task = filteredTasks.find(({ id: taskId }) => taskId === id);
      if (!task) {
        return;
      }
      const daySchedule = getScheduleByDate(task.schedules, { date, today });
      onComplete(task, ScheduleEntryState.Complete, {
        referenceDate: date,
        toggleDayEntry: !!daySchedule?.frequency,
      });
    },
    [onComplete, filteredTasks, today],
  );

  const events = useMemo(
    () => [
      ...(user?.settings?.upcoming?.hideExternalCalendarEvents
        ? []
        : externalCalendarEvents),
      ...taskEvents,
      ...goalDeadlineEvents,
    ],
    [
      externalCalendarEvents,
      goalDeadlineEvents,
      taskEvents,
      user?.settings?.upcoming?.hideExternalCalendarEvents,
    ],
  );

  return (
    <FullCalendarMonth
      fullCalendarRef={fullCalendarRef}
      tasks={filteredTasks}
      goals={goals}
      lifeAreas={lifeAreas}
      weekStartsOn={weekStartsOn}
      onCompleteTask={_onComplete}
      onClickEvent={_openEventDetail}
      referenceDate={referenceDate}
      events={events}
      eventClassNames={eventClasNames}
      eventChange={onEventChange}
      drop={onDrop}
      dateClick={onDateTime}
      eventResizableFromStart={false}
      eventDurationEditable={false}
      editable
      droppable
    />
  );
};
